import React from 'react';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import { COMPETENCY_BY_ID } from '@evaltick/shared';
import { BuilderChip } from '@/components/BuilderChip';
import {
  DetailSheet,
  DetailSheetActions,
  DetailSheetCol,
  DetailSheetEmpty,
  DetailSheetHead,
  DetailSheetLink,
  DetailSheetRow,
  DetailSheetSection,
  type DetailSheetActionItem,
} from '@/components/DetailSheet';
import { competencyChipColor, typeChipColor } from '@/utils/builderChipColors';
import { formatDate } from '@/utils/formatters';
import { QUESTION_TYPE_LABELS } from '@/utils/questionTypeLabels';
import type { QuestionWithQuestionSets } from '@/services/api';

interface QuestionDetailDrawerProps {
  question: QuestionWithQuestionSets;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onNavigateToQuestionSet: (id: string) => void;
}

/** Read-only "Question" drawer opened on row click — matches the reference design's
 * `openDetailDrawer`, distinct from the edit form opened via the pencil icon. */
export const QuestionDetailDrawer: React.FC<QuestionDetailDrawerProps> = ({
  question,
  onEdit,
  onDuplicate,
  onDelete,
  onNavigateToQuestionSet,
}) => {
  const competencyName = COMPETENCY_BY_ID[question.competencyId]?.name ?? question.competencyId;

  const actions: DetailSheetActionItem[] = [
    {
      key: 'edit',
      label: 'Edit question',
      icon: <Pencil style={{ width: '1em', height: '1em' }} />,
      onClick: onEdit,
    },
    {
      key: 'duplicate',
      label: 'Duplicate',
      icon: <Copy style={{ width: '1em', height: '1em' }} />,
      onClick: onDuplicate,
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <Trash2 style={{ width: '1em', height: '1em' }} />,
      danger: true,
      onClick: onDelete,
    },
  ];

  return (
    <DetailSheet>
      <DetailSheetHead
        title={question.text}
        tags={
          <>
            <BuilderChip tint={competencyChipColor(question.competencyId)}>
              {competencyName}
            </BuilderChip>
            <BuilderChip tint={typeChipColor(question.questionType)}>
              {QUESTION_TYPE_LABELS[question.questionType]}
            </BuilderChip>
          </>
        }
      />

      <DetailSheetRow>
        <DetailSheetCol label="Created" value={formatDate(question.createdAt)} />
        <DetailSheetCol label="Updated" value={formatDate(question.updatedAt)} />
      </DetailSheetRow>

      <DetailSheetSection label="Used in">
        {question.questionSets.length === 0 ? (
          <DetailSheetEmpty>Not used in any question set</DetailSheetEmpty>
        ) : (
          question.questionSets.map((qs) => (
            <DetailSheetLink key={qs.id} name={qs.name} onClick={() => onNavigateToQuestionSet(qs.id)} />
          ))
        )}
      </DetailSheetSection>

      <DetailSheetActions items={actions} />
    </DetailSheet>
  );
};

export default QuestionDetailDrawer;
